import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

// Pre-randomized confetti particles so they stay stable across re-renders.
const makeConfetti = (count) =>
  Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * Math.PI * 2 + Math.random() * 0.4;
    const distance = 180 + Math.random() * 140;
    return {
      id: i,
      x: Math.cos(angle) * distance,
      y: Math.sin(angle) * distance,
      size: 6 + Math.random() * 10,
      rotation: Math.random() * 360,
      duration: 0.9 + Math.random() * 0.6,
      delay: Math.random() * 0.25,
      shape: ["star", "circle", "ring", "spark"][i % 4],
      color: ["#0065F8", "#00CAFF", "#3DA9FF", "#FFFFFF"][i % 4],
    };
  });

const ConfettiPiece = ({ p }) => {
  const common = {
    position: "absolute",
    left: "50%",
    top: "50%",
    width: p.size,
    height: p.size,
    pointerEvents: "none",
  };
  return (
    <motion.div
      style={common}
      initial={{ x: 0, y: 0, opacity: 0, scale: 0, rotate: 0 }}
      animate={{
        x: p.x,
        y: p.y,
        opacity: [0, 1, 1, 0],
        scale: [0, 1.1, 1, 0.8],
        rotate: p.rotation,
      }}
      transition={{ duration: p.duration, ease: "easeOut", delay: p.delay, times: [0, 0.2, 0.7, 1] }}
    >
      {p.shape === "star" && (
        <svg viewBox="0 0 24 24" fill={p.color} width="100%" height="100%">
          <path d="M12 2l2.39 4.84L20 7.27l-4 3.91.94 5.5L12 14.77l-4.94 2.92.94-5.5-4-3.91 5.61-.43L12 2z" />
        </svg>
      )}
      {p.shape === "circle" && (
        <div style={{ width: "100%", height: "100%", borderRadius: "50%", background: p.color, boxShadow: `0 0 8px ${p.color}` }} />
      )}
      {p.shape === "ring" && (
        <div style={{ width: "100%", height: "100%", borderRadius: "50%", border: `2.5px solid ${p.color}` }} />
      )}
      {p.shape === "spark" && (
        <svg viewBox="0 0 24 24" fill="none" stroke={p.color} strokeWidth="3" strokeLinecap="round" width="100%" height="100%">
          <path d="M12 3v8M12 13v8M3 12h8M13 12h8" />
        </svg>
      )}
    </motion.div>
  );
};

const ProfileSwitchAnimation = ({ oldProfile, newProfile, onComplete }) => {
  // Stages: flyIn (old appears) → portal (sparkle burst) → reveal (new appears) → outro (fade)
  const [stage, setStage] = useState("flyIn");

  // Keep onComplete in a ref so the timer effect runs exactly once.
  // (ProfileContext recreates `onComplete` whenever it re-renders, e.g. when
  // setCurrentProfile fires mid-animation. Including it in deps would reset
  // all timers and cause the reveal stage to play twice.)
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);

  useEffect(() => {
    const t1 = setTimeout(() => setStage("portal"), 700);
    const t2 = setTimeout(() => setStage("reveal"), 1200);
    const t3 = setTimeout(() => setStage("outro"), 3300);
    const t4 = setTimeout(() => onCompleteRef.current && onCompleteRef.current(), 3800);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memo so the random particle positions don't reshuffle every render
  const confetti = useMemo(() => makeConfetti(28), []);
  const orbits = useMemo(
    () => Array.from({ length: 8 }, (_, i) => ({
      id: i,
      delay: i * 0.1,
      angle: (i / 8) * 360,
      color: i % 2 === 0 ? "#0065F8" : "#00CAFF",
    })),
    []
  );

  if (!oldProfile || !newProfile) return null;

  const showOld = stage === "flyIn";
  const showPortal = stage === "portal";
  const showNew = stage === "reveal" || stage === "outro";

  return (
    <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center overflow-hidden" style={FREDOKA}>
      {/* Backdrop — cream-blue brand blur */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: stage === "outro" ? 0 : 1 }}
        transition={{ duration: stage === "outro" ? 0.5 : 0.3, ease: "easeInOut" }}
        className="absolute inset-0"
        style={{
          background: "linear-gradient(135deg, rgba(11,26,51,0.55) 0%, rgba(0,101,248,0.42) 50%, rgba(0,202,255,0.42) 100%)",
          backdropFilter: "blur(16px)",
        }}
      />

      {/* Decorative drifting orbs in the background */}
      <motion.div
        className="absolute"
        style={{ top: "20%", left: "15%", width: 220, height: 220, borderRadius: "50%", background: "rgba(0,202,255,0.30)", filter: "blur(40px)" }}
        animate={{ y: [0, -20, 0], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="absolute"
        style={{ bottom: "18%", right: "12%", width: 260, height: 260, borderRadius: "50%", background: "rgba(0,101,248,0.35)", filter: "blur(40px)" }}
        animate={{ y: [0, 20, 0], opacity: [0.4, 0.6, 0.4] }}
        transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
      />

      {/* Stage content */}
      <div className="relative w-full h-full flex items-center justify-center">
        <AnimatePresence mode="wait">
          {/* Stage 1 — Old profile present, will dissolve */}
          {showOld && (
            <motion.div
              key="old"
              initial={{ scale: 0.7, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.5, opacity: 0, filter: "blur(8px)", transition: { duration: 0.4 } }}
              transition={{ duration: 0.5, ease: [0.34, 1.56, 0.64, 1] }}
              className="flex flex-col items-center gap-5"
            >
              <ProfileAvatar profile={oldProfile} ringColor="rgba(255,255,255,0.55)" />
              <Nameplate eyebrow="Leaving" name={oldProfile.name} accent="rgba(255,255,255,0.55)" />
            </motion.div>
          )}

          {/* Stage 2 — Portal burst */}
          {showPortal && (
            <motion.div
              key="portal"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.6, transition: { duration: 0.4 } }}
              transition={{ duration: 0.4 }}
              className="relative flex items-center justify-center"
              style={{ width: 320, height: 320 }}
            >
              {/* Spinning conic-gradient portal ring */}
              <motion.div
                className="absolute inset-0"
                style={{
                  borderRadius: "50%",
                  background: "conic-gradient(from 0deg, #0065F8 0%, #00CAFF 25%, #FFFFFF 50%, #00CAFF 75%, #0065F8 100%)",
                  WebkitMask: "radial-gradient(circle at center, transparent 56%, black 60%)",
                  mask: "radial-gradient(circle at center, transparent 56%, black 60%)",
                  filter: "drop-shadow(0 0 40px rgba(0,101,248,0.50))",
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1.4, ease: "linear", repeat: Infinity }}
              />
              {/* Pulsing inner core */}
              <motion.div
                className="absolute"
                style={{
                  width: 168,
                  height: 168,
                  borderRadius: "50%",
                  background: "radial-gradient(circle at center, rgba(255,255,255,0.95) 0%, rgba(0,202,255,0.85) 40%, rgba(0,101,248,0.0) 75%)",
                }}
                animate={{ scale: [0.8, 1.15, 0.95, 1.2] }}
                transition={{ duration: 0.7, ease: "easeOut" }}
              />
            </motion.div>
          )}

          {/* Stage 3 — New profile reveal */}
          {showNew && (
            <motion.div
              key="new"
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{
                scale: stage === "outro" ? 1.1 : 1,
                opacity: stage === "outro" ? 0 : 1,
                filter: stage === "outro" ? "blur(8px)" : "blur(0px)",
              }}
              transition={{ duration: stage === "outro" ? 0.5 : 0.6, ease: [0.34, 1.56, 0.64, 1] }}
              className="relative flex flex-col items-center gap-6"
            >
              {/* Orbiting particles around the avatar */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                {orbits.map((o) => (
                  <motion.div
                    key={o.id}
                    className="absolute"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: o.color,
                      boxShadow: `0 0 12px ${o.color}`,
                      transformOrigin: "center",
                    }}
                    initial={{ x: 0, y: 0, opacity: 0 }}
                    animate={{
                      x: Math.cos((o.angle + 360) * Math.PI / 180) * 130,
                      y: Math.sin((o.angle + 360) * Math.PI / 180) * 130,
                      opacity: [0, 1, 1, 0],
                      scale: [0, 1, 1, 0.6],
                      rotate: 720,
                    }}
                    transition={{ duration: 2.4, ease: "easeOut", delay: o.delay, times: [0, 0.2, 0.8, 1] }}
                  />
                ))}
              </div>

              {/* Confetti burst */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                {confetti.map((p) => (
                  <ConfettiPiece key={p.id} p={p} />
                ))}
              </div>

              <ProfileAvatar profile={newProfile} ringColor="#FFFFFF" glow showCheck />
              <Nameplate eyebrow="Switched to" name={newProfile.name} accent="#FFFFFF" highlight />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────── */
/* Avatar + nameplate subcomponents                                    */
/* ─────────────────────────────────────────────────────────────────── */

const ProfileAvatar = ({ profile, ringColor, glow = false, showCheck = false }) => (
  <motion.div
    className="relative"
    animate={{ scale: [1, 1.06, 1] }}
    transition={{ duration: 0.9, ease: "easeInOut", repeat: glow ? Infinity : 0 }}
  >
    {/* Outer halo */}
    {glow && (
      <motion.div
        className="absolute inset-0"
        style={{
          borderRadius: "50%",
          background: "radial-gradient(circle at center, rgba(0,202,255,0.55) 0%, rgba(0,101,248,0) 70%)",
          filter: "blur(8px)",
          transform: "scale(1.6)",
        }}
        animate={{ opacity: [0.5, 0.9, 0.5], scale: [1.5, 1.7, 1.5] }}
        transition={{ duration: 1.8, ease: "easeInOut", repeat: Infinity }}
      />
    )}
    {/* Avatar ring */}
    <div
      className="relative overflow-hidden"
      style={{
        width: 152,
        height: 152,
        borderRadius: "50%",
        background: "#FFFFFF",
        border: `5px solid ${ringColor}`,
        boxShadow: glow
          ? "0px 6px 0px #073B8E, 0px 0px 0px 4px rgba(0,101,248,0.35), 0px 0px 60px rgba(0,202,255,0.55)"
          : "0px 5px 0px rgba(11,26,51,0.18), 0px 0px 0px 4px rgba(255,255,255,0.18)",
      }}
    >
      {profile.avatar_url ? (
        <img
          src={profile.avatar_url}
          alt={profile.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
        />
      ) : null}
      <div
        className={`w-full h-full flex items-center justify-center ${profile.avatar_url ? "hidden" : "flex"}`}
        style={{ background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)" }}
      >
        <span style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: 56 }}>
          {profile.name?.charAt(0)?.toUpperCase() || "?"}
        </span>
      </div>
    </div>

    {/* Success check badge */}
    {showCheck && (
      <motion.div
        className="absolute"
        style={{
          bottom: -4,
          right: -4,
          width: 44,
          height: 44,
          borderRadius: "50%",
          background: "#FFFFFF",
          color: "#0065F8",
          border: "4px solid #FFFFFF",
          boxShadow: "0px 3px 0px rgba(7,59,142,0.30), 0px 0px 20px rgba(0,101,248,0.50)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        initial={{ scale: 0, rotate: -180 }}
        animate={{ scale: 1, rotate: 0 }}
        transition={{ delay: 0.3, type: "spring", stiffness: 360, damping: 14 }}
      >
        <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="3.4" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
      </motion.div>
    )}
  </motion.div>
);

const Nameplate = ({ eyebrow, name, accent, highlight = false }) => (
  <motion.div
    className="relative overflow-hidden px-7 py-3.5 text-center"
    style={{
      background: highlight
        ? "linear-gradient(180deg, #FFFFFF 0%, #F5FAFF 100%)"
        : "rgba(255,255,255,0.10)",
      border: highlight ? "2px solid rgba(11,26,51,0.06)" : `2px solid ${accent}`,
      borderRadius: 20,
      boxShadow: highlight
        ? "0px 5px 0px rgba(7,59,142,0.30), 0px 14px 28px rgba(0,101,248,0.30)"
        : "0px 3px 0px rgba(11,26,51,0.18)",
      backdropFilter: highlight ? "none" : "blur(8px)",
      minWidth: 200,
    }}
  >
    <div
      style={{
        ...FREDOKA,
        color: highlight ? "#0065F8" : "rgba(255,255,255,0.85)",
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.20em",
        textTransform: "uppercase",
      }}
    >
      {eyebrow}
    </div>
    <div
      style={{
        ...FREDOKA,
        color: highlight ? "#0B1A33" : "#FFFFFF",
        fontWeight: 700,
        fontSize: 26,
        textTransform: "uppercase",
        letterSpacing: "-0.01em",
        lineHeight: 1.1,
        marginTop: 4,
      }}
    >
      {name}
    </div>
    {highlight && (
      <motion.div
        className="mx-auto mt-2.5"
        style={{
          height: 3,
          borderRadius: 999,
          background: "linear-gradient(90deg, #0065F8 0%, #00CAFF 100%)",
        }}
        initial={{ width: 0 }}
        animate={{ width: "70%" }}
        transition={{ duration: 0.45, delay: 0.25, ease: "easeOut" }}
      />
    )}
  </motion.div>
);

export default ProfileSwitchAnimation;
